import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../lib/supabase';

const AuthContext = createContext();

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [supabase, setSupabase] = useState(null);
    const navigate = useNavigate();    useEffect(() => {
        let mounted = true;

        async function initializeAuth() {
            try {
                // Try using the global helper function first if available
                let client;
                if (typeof window !== 'undefined' && typeof window.ensureSupabaseClient === 'function') {
                    client = await window.ensureSupabaseClient();
                    if (client) {
                        console.log('Using Supabase client from global helper');
                        setSupabase(client);
                    } else {
                        // Fallback to traditional initialization
                        client = getSupabaseClient();
                        if (!client) {
                            console.warn('Supabase client not available, will retry');
                            return;
                        }
                        setSupabase(client);
                    }
                } else {
                    // Fallback to traditional initialization
                    client = getSupabaseClient();
                    if (!client) {
                        console.warn('Supabase client not available, will retry');
                        return;
                    }
                    setSupabase(client);
                }

                // Check active sessions and sets the user
                const { data: { session } } = await client.auth.getSession();
                if (mounted && session?.user) {
                    try {
                        console.log('Session user:', session.user);
                        
                        // Get user metadata including role
                        const { data: profile, error: profileError } = await client
                            .from('profiles')
                            .select('*')
                            .eq('id', session.user.id)
                            .single();
                        
                        console.log('Profile fetch result:', { profile, profileError });
                        
                        if (profileError) {
                            console.error('Error fetching profile:', profileError);
                            // If profile doesn't exist, create it
                            if (profileError.code === 'PGRST116') {
                                console.log('Profile not found, creating new profile...');
                                const { data: newProfile, error: createError } = await client
                                    .from('profiles')
                                    .insert([
                                        {
                                            id: session.user.id,
                                            email: session.user.email,
                                            role: 'admin' // Set as admin for your email
                                        }
                                    ])
                                    .select()
                                    .single();
                                
                                if (createError) {
                                    console.error('Error creating profile:', createError);
                                } else {
                                    console.log('Created new profile:', newProfile);
                                    profile = newProfile;
                                }
                            }
                        }
                        
                        // Special case for your email
                        let userRole = 'user';
                        let userProfile = profile;
                        
                        if (session.user.email === 'macauleylynch1@gmail.com') {
                            console.log('Admin user detected, ensuring admin role...');
                            const { data: updatedProfile, error: updateError } = await client
                                .from('profiles')
                                .update({ role: 'admin' })
                                .eq('id', session.user.id)
                                .select()
                                .single();
                            
                            if (updateError) {
                                console.error('Error updating admin role:', updateError);
                            } else {
                                console.log('Updated profile to admin:', updatedProfile);
                                userProfile = updatedProfile;
                                userRole = 'admin';
                            }
                        }
                        
                        const userWithRole = {
                            ...session.user,
                            role: userProfile?.role || userRole,
                            metadata: {
                                ...session.user.user_metadata,
                                role: userProfile?.role || userRole
                            }
                        };
                        
                        console.log('Final user object:', userWithRole);
                        setUser(userWithRole);
                    } catch (error) {
                        console.error('Error in profile handling:', error);
                        // Set user without role if profile handling fails
                        setUser(session.user);
                    }
                } else if (mounted) {
                    setUser(null);
                }
                setLoading(false);

                // Listen for changes on auth state
                const { data: { subscription } } = client.auth.onAuthStateChange(async (_event, session) => {
                    if (mounted && session?.user) {
                        try {
                            console.log('Session user:', session.user);
                            
                            // Get user metadata including role
                            const { data: profile, error: profileError } = await client
                                .from('profiles')
                                .select('*')
                                .eq('id', session.user.id)
                                .single();
                            
                            console.log('Profile fetch result:', { profile, profileError });
                            
                            if (profileError) {
                                console.error('Error fetching profile:', profileError);
                                // If profile doesn't exist, create it
                                if (profileError.code === 'PGRST116') {
                                    console.log('Profile not found, creating new profile...');
                                    const { data: newProfile, error: createError } = await client
                                        .from('profiles')
                                        .insert([
                                            {
                                                id: session.user.id,
                                                email: session.user.email,
                                                role: 'admin' // Set as admin for your email
                                            }
                                        ])
                                        .select()
                                        .single();
                                    
                                    if (createError) {
                                        console.error('Error creating profile:', createError);
                                    } else {
                                        console.log('Created new profile:', newProfile);
                                        profile = newProfile;
                                    }
                                }
                            }
                            
                            // Special case for your email
                            let userRole = 'user';
                            let userProfile = profile;
                            
                            if (session.user.email === 'macauleylynch1@gmail.com') {
                                console.log('Admin user detected, ensuring admin role...');
                                const { data: updatedProfile, error: updateError } = await client
                                    .from('profiles')
                                    .update({ role: 'admin' })
                                    .eq('id', session.user.id)
                                    .select()
                                    .single();
                                
                                if (updateError) {
                                    console.error('Error updating admin role:', updateError);
                                } else {
                                    console.log('Updated profile to admin:', updatedProfile);
                                    userProfile = updatedProfile;
                                    userRole = 'admin';
                                }
                            }
                            
                            const userWithRole = {
                                ...session.user,
                                role: userProfile?.role || userRole,
                                metadata: {
                                    ...session.user.user_metadata,
                                    role: userProfile?.role || userRole
                                }
                            };
                            
                            console.log('Final user object:', userWithRole);
                            setUser(userWithRole);
                        } catch (error) {
                            console.error('Error in profile handling:', error);
                            // Set user without role if profile handling fails
                            setUser(session.user);
                        }
                    } else if (mounted) {
                        setUser(null);
                    }
                    setLoading(false);
                });

                return () => {
                    mounted = false;
                    subscription?.unsubscribe();
                };
            } catch (error) {
                console.error('Auth initialization error:', error);
                if (mounted) {
                    setLoading(false);
                }
            }
        }

        initializeAuth();
    }, []);    const handleLogin = async (credentials) => {
        try {
            if (!supabase) {
                throw new Error('Supabase client not initialized');
            }
            const { data, error } = await supabase.auth.signInWithPassword(credentials);
            if (error) throw error;

            // Get user metadata including role
            const { data: profile } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', data.user.id)
                .single();

            setUser({
                ...data.user,
                role: profile?.role || 'user',
                metadata: {
                    ...data.user.user_metadata,
                    role: profile?.role || 'user'
                }
            });
            
            // Ensure we redirect to home
            navigate('/', { replace: true });
            return data;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    };const handleRegister = async (userData) => {
        try {
            if (!supabase) {
                throw new Error('Supabase client not initialized');
            }
            const { data, error } = await supabase.auth.signUp(userData);
            if (error) throw error;

            try {
                // Check if a profile already exists
                const { data: existingProfile } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', data.user.id)
                    .single();
                
                // Only create a profile if one doesn't exist
                if (!existingProfile) {
                    // Create profile with default role
                    const { error: profileError } = await supabase
                        .from('profiles')
                        .insert([
                            {
                                id: data.user.id,
                                role: 'user',
                                email: data.user.email
                            }
                        ]);

                    if (profileError) {
                        console.error('Profile creation error:', profileError);
                        // Continue even if profile creation fails
                    }
                }
            } catch (profileError) {
                console.error('Profile handling error:', profileError);
                // Continue with the registration process even if profile handling fails
            }

            setUser({
                ...data.user,                role: 'user',
                metadata: {
                    ...data.user.user_metadata,
                    role: 'user'
                }
            });
            
            navigate('/', { replace: true });
            return data;
        } catch (error) {
            console.error('Registration error:', error);
            throw error;
        }
    };

    const handleLogout = async () => {
        try {
            if (!supabase) {
                throw new Error('Supabase client not initialized');
            }
            const { error } = await supabase.auth.signOut();
            if (error) throw error;
            setUser(null);
            navigate('/');
        } catch (error) {
            console.error('Logout error:', error);
            throw error;
        }
    };    const value = {
        user,
        loading,
        handleLogin,
        handleRegister,
        handleLogout,
        setUser
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}